import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {
  AutoMapperColumnJoinType,
  AutoMapperMode,
} from 'analysis-tool/data-frame-creation/column-mapping/ColumnMappingUtil';
import DataFrameColumnAutoMapper from 'analysis-tool/data-frame-creation/column-mapping/DataFrameColumnAutoMapper';
import getUserColumns from 'data-frames/util/getUserColumns';
import {
  FONT_PROXIMA_13,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
  FONT_WEIGHT_PROXIMA_REGULAR,
} from 'styles/FontConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import * as Colors from 'ui-library/Colors';

import {DataFrameColumnMappingTable, DataFrameColumnMappingTableProps} from './DataFrameColumnMappingTable';

export type ReplaceTableColumnMappingProps = DataFrameColumnMappingTableProps & {
  isManuallyChanged: boolean;
  setIsManuallyChanged: (isManuallyChanged: boolean) => void;
};

export function ReplaceTableColumnMapping({
  isManuallyChanged,
  setIsManuallyChanged,
  ...dataFrameColumnMappingTableProps
}: ReplaceTableColumnMappingProps): JSX.Element {
  const {newDataFrameColumns, templateDataFrameColumns, setSelectedColumns} =
    dataFrameColumnMappingTableProps;

  const aligningColumns = useMemo(
    () => getUserColumns({columns: newDataFrameColumns ?? []}),
    [newDataFrameColumns]
  );

  return (
    <div>
      <div className={css(styles.helpText)}>
        Map columns to specify how any existing downstream transforms will use your new table.
      </div>
      <DataFrameColumnAutoMapper
        templateColumns={templateDataFrameColumns}
        aligningColumns={aligningColumns}
        setSelectedTemplateColumns={noop}
        setSelectedAligningColumns={setSelectedColumns}
        initialAutoMapperMode={AutoMapperMode.NAME}
        initialAutoMapperColumnJoinType={AutoMapperColumnJoinType.KEEP_TEMPLATE}
        allowModeChange={true}
        allowColumnJoinTypeChange={false}
        autoMap={!isManuallyChanged}
      />
      <Row className={css(styles.headerRow)}>
        <Col md={6}>Original table columns</Col>
        <Col md={6}>New table columns</Col>
      </Row>
      <DataFrameColumnMappingTable
        {...dataFrameColumnMappingTableProps}
        setSelectedColumns={(columns) => {
          setIsManuallyChanged(true);
          dataFrameColumnMappingTableProps.setSelectedColumns(columns);
        }}
        noColumnSelectedText="Skip mapping"
      />
    </div>
  );
}

const styles = StyleSheet.create({
  autoMapper: {
    width: 175,
    paddingTop: 20,
  },
  headerRow: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    paddingTop: 20,
  },
  helpText: {
    color: Colors.GRAY_DARKER,
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    paddingBottom: 20,
  },
});
